/**
 * LOS BLOQUES DE LA JORNADA.
 *
 * Un trabajo de 8 horas que arranca a las 7 no va "de 7 a 15": a las 12 la
 * gente para a almorzar, así que en la calle es 7–12, almuerzo 12–1, y 1–4.
 * Este helper parte las horas de un trabajo en esos bloques —mañana, almuerzo,
 * tarde, y días siguientes si no cabe— para poder MOSTRARLOS separados, con la
 * hora de almuerzo visible.
 *
 * Es solo para pintar: quién calcula el fin de verdad es la base
 * (fn_fin_operativo). Aquí se reconstruyen los tramos para el ojo, en la misma
 * jornada que usa la base: 07:00–17:00, almuerzo 12:00–13:00, sábado hasta las
 * 12:00, domingo como entre semana (0266). Trabaja en hora local (Colombia),
 * que es donde está la gente.
 */

const APERTURA = 7 // 07:00
const CIERRE = 17 // 17:00 (lun–vie)
const CIERRE_SABADO = 12 // 12:00
const ALMUERZO_INI = 12 // 12:00
const ALMUERZO_FIN = 13 // 13:00

/*
 * EL ALMUERZO YA NO TIENE HORA (0235 / 0317).
 *
 * El técnico marca «Salir a almuerzo» cuando le toca, y en ese momento se
 * corre lo que sigue (fn_correr_por_almuerzo). La jornada de lunes a viernes
 * cierra a las 17:00 (5:00 p. m.), permitiendo programar actividades hasta las
 * 5:00 p. m. (0317). Esto tiene que decir lo mismo que fn_fin_operativo.
 */
export const ALMUERZO_FIJO = false

/**
 * LOS DOMINGOS SE TRABAJA (0266). Johan: «los domingos también se trabajan».
 * Antes el domingo se saltaba y un trabajo programado el domingo a la 1:00
 * p. m. aparecía el lunes a las 7:00. Tiene que decir lo mismo que
 * configuracion.trabaja_domingo, que es lo que usa fn_fin_operativo.
 */
export const TRABAJA_DOMINGO = true

export type Segmento = {
  tipo: 'trabajo' | 'almuerzo'
  inicio: Date
  fin: Date
}

/** Minutos desde medianoche de una fecha, en hora local. */
function minutosDelDia(d: Date): number {
  return d.getHours() * 60 + d.getMinutes()
}

/** Una copia de la fecha puesta a una hora:minuto de ESE mismo día. */
function aLaHora(d: Date, horas: number, minutos = 0): Date {
  const x = new Date(d)
  x.setHours(horas, minutos, 0, 0)
  return x
}

/** El cierre de la jornada de ese día (sábado más temprano; domingo no hay). */
function cierreDelDia(d: Date): number {
  const dia = d.getDay() // 0 domingo, 6 sábado
  if (dia === 0 && !TRABAJA_DOMINGO) return APERTURA // domingo sin trabajo: se salta
  if (dia === 6) return CIERRE_SABADO
  return CIERRE
}

/** Si ese día hay hueco fijo de almuerzo que partir. */
function hayAlmuerzoFijo(d: Date): boolean {
  return ALMUERZO_FIJO && cierreDelDia(d) > ALMUERZO_FIN
}

/** Avanza el cursor al comienzo de la próxima jornada hábil (salta domingo). */
function siguienteDiaHabil(d: Date): Date {
  let x = new Date(d)
  do {
    x = aLaHora(x, APERTURA)
    x.setDate(x.getDate() + 1)
  } while (!TRABAJA_DOMINGO && x.getDay() === 0)
  return aLaHora(x, APERTURA)
}

/**
 * Parte `horasTrabajo` de trabajo, empezando en `inicioISO`, en segmentos de
 * jornada con el almuerzo intercalado. Devuelve [] si no hay datos.
 */
export function bloquesJornada(
  inicioISO: string | null | undefined,
  horasTrabajo: number | null | undefined,
): Segmento[] {
  if (!inicioISO || !horasTrabajo || horasTrabajo <= 0) return []

  const segmentos: Segmento[] = []
  let restanteMin = Math.round(horasTrabajo * 60)
  let cursor = new Date(inicioISO)
  if (isNaN(cursor.getTime())) return []

  // Tope de seguridad: nunca más de ~30 tramos (mucho más que cualquier obra).
  let guarda = 0

  while (restanteMin > 0 && guarda++ < 40) {
    // Domingo, o pasado el cierre: al siguiente día hábil a las 7.
    if (
      (!TRABAJA_DOMINGO && cursor.getDay() === 0) ||
      minutosDelDia(cursor) >= cierreDelDia(cursor) * 60
    ) {
      cursor = siguienteDiaHabil(cursor)
      continue
    }
    // Antes de abrir: al arranque de la jornada.
    if (minutosDelDia(cursor) < APERTURA * 60) {
      cursor = aLaHora(cursor, APERTURA)
      continue
    }

    const min = minutosDelDia(cursor)

    // Dentro del almuerzo (solo días con jornada de tarde): se emite el
    // almuerzo y se salta al otro lado.
    if (hayAlmuerzoFijo(cursor) && min >= ALMUERZO_INI * 60 && min < ALMUERZO_FIN * 60) {
      segmentos.push({
        tipo: 'almuerzo',
        inicio: aLaHora(cursor, ALMUERZO_INI),
        fin: aLaHora(cursor, ALMUERZO_FIN),
      })
      cursor = aLaHora(cursor, ALMUERZO_FIN)
      continue
    }

    // El próximo borde: o empieza el almuerzo, o cierra la jornada.
    const cierreMin = cierreDelDia(cursor) * 60
    const bordeMin =
      hayAlmuerzoFijo(cursor) && min < ALMUERZO_INI * 60 ? ALMUERZO_INI * 60 : cierreMin

    const disponibleMin = bordeMin - min
    if (disponibleMin <= 0) {
      cursor = aLaHora(cursor, Math.floor(bordeMin / 60), bordeMin % 60)
      continue
    }

    const trabajaMin = Math.min(restanteMin, disponibleMin)
    const fin = new Date(cursor.getTime() + trabajaMin * 60000)
    segmentos.push({ tipo: 'trabajo', inicio: new Date(cursor), fin })
    restanteMin -= trabajaMin
    cursor = fin
  }

  return segmentos
}

/** 7:00 a. m., 12:00 m., 1:00 p. m. — hora local de Colombia. */
export function horaLocal(d: Date): string {
  return new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d)
}

/** El día, para separar cuando la obra cruza a otra fecha (ej. "vie 18"). */
export function diaCorto(d: Date): string {
  return new Intl.DateTimeFormat('es-CO', { weekday: 'short', day: 'numeric' }).format(d)
}

/** "23 sept." — día y mes corto, para el rango de una obra de varios días. */
export function fechaCorta(d: Date): string {
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' }).format(d)
}

/**
 * El tramo total de una obra: cuántos DÍAS hábiles ocupa y de cuándo a cuándo.
 *
 * Sale de los mismos bloques de jornada que pinta HorarioObra, contando solo el
 * trabajo (el almuerzo no suma día). Sirve para avisarle al técnico, de entrada,
 * que la obra es de varios días —y que el primer día reporta avances, no
 * termina—. Devuelve null si no hay datos o si cabe en un solo día.
 */
export function tramoDeObra(
  inicioISO: string | null | undefined,
  horasTrabajo: number | null | undefined,
): { dias: number; desde: Date; hasta: Date } | null {
  const trabajo = bloquesJornada(inicioISO, horasTrabajo).filter((s) => s.tipo === 'trabajo')
  if (trabajo.length === 0) return null

  const dias = new Set(trabajo.map((s) => s.inicio.toDateString())).size
  return { dias, desde: trabajo[0].inicio, hasta: trabajo[trabajo.length - 1].fin }
}
